import DOMPurify from "dompurify";

const BASE_STYLE = `
html { color-scheme: light; background: #ffffff; }
body {
  margin: 0;
  padding: 16px 18px;
  background: #ffffff;
  color: #1c1917;
  font-family: "Segoe UI", Calibri, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
img, video { max-width: 100%; }
table { max-width: 100%; }
`.trim();

/** Überschreibt nur Techniken, die Schrift unsichtbar machen, nicht die Farben der Mail. */
const GUARD_STYLE = `
* {
  -webkit-text-fill-color: currentcolor !important;
  mix-blend-mode: normal !important;
}
`.trim();

let linkHooksReady = false;

function ensureLinkHooks(): void {
  if (linkHooksReady) return;
  linkHooksReady = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

function matchBrace(css: string, openIndex: number): number {
  let depth = 0;
  let quote = "";
  for (let i = openIndex; i < css.length; i += 1) {
    const ch = css[i] ?? "";
    if (quote) {
      if (ch === "\\") {
        i += 1;
        continue;
      }
      if (ch === quote) quote = "";
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === "/" && css[i + 1] === "*") {
      const end = css.indexOf("*/", i + 2);
      if (end === -1) return -1;
      i = end + 1;
      continue;
    }
    if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function normalizePrelude(prelude: string): string {
  return prelude.replace(/\/\*[\s\S]*?\*\//g, "").toLowerCase().replace(/\s+/g, "");
}

function isDarkModeMedia(prelude: string): boolean {
  const normalized = normalizePrelude(prelude);
  if (!normalized.includes("@media") || !normalized.includes("prefers-color-scheme")) return false;
  return normalized.includes("dark") && !normalized.includes("light");
}

function withoutDarkSelectors(prelude: string): string {
  const comments: string[] = [];
  const commentless = prelude.replace(/\/\*[\s\S]*?\*\//g, (comment) => {
    comments.push(comment);
    return "";
  });
  const parts = commentless.split(",");
  const kept = parts.filter((part) => {
    const normalized = part.toLowerCase().replace(/\s+/g, "");
    return !(
      normalized.includes("data-ogsc") ||
      normalized.includes("data-ogsb") ||
      normalized.includes("data-ogab") ||
      normalized.includes("gmail-blend")
    );
  });
  if (kept.length === parts.length) return prelude;
  if (kept.every((part) => part.trim() === "")) return "";
  const restored = comments.length > 0 ? `${comments.join("")}${kept.join(",")}` : kept.join(",");
  return restored;
}

/** Entfernt Dark-Mode-Regeln, die auf weißem Grund unsichtbare Schrift erzeugen. */
export function stripUnreadableMailCss(css: string): string {
  let out = "";
  let i = 0;
  while (i < css.length) {
    const open = css.indexOf("{", i);
    if (open === -1) {
      out += css.slice(i);
      break;
    }
    const close = matchBrace(css, open);
    if (close === -1) {
      out += css.slice(i);
      break;
    }
    const prelude = css.slice(i, open);
    const body = css.slice(open + 1, close);
    if (isDarkModeMedia(prelude)) {
      i = close + 1;
      continue;
    }
    const keptPrelude = withoutDarkSelectors(prelude);
    if (keptPrelude.trim() === "") {
      i = close + 1;
      continue;
    }
    const normalized = normalizePrelude(keptPrelude);
    const nested =
      normalized.includes("@media") || normalized.includes("@supports") || normalized.includes("@layer");
    const nextBody = nested ? stripUnreadableMailCss(body) : body;
    out += `${keptPrelude}{${nextBody}}`;
    i = close + 1;
  }
  return out;
}

function rewriteStyleTags(html: string): string {
  return html.replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (_match, attrs: string, css: string) => {
    return `<style${attrs}>${stripUnreadableMailCss(css)}</style>`;
  });
}

function visibleMailText(html: string): string {
  return html
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function mailHtmlHasVisibleContent(html: string): boolean {
  if (visibleMailText(html).length > 0) return true;
  return /<img\b/i.test(html);
}

function injectFrameChrome(documentHtml: string): string {
  const base = `<meta charset="utf-8"><base target="_blank"><style id="campdesk-mail-base">${BASE_STYLE}</style>`;
  const guard = `<style id="campdesk-mail-guard">${GUARD_STYLE}</style>`;
  let next = documentHtml.replace(/<meta\b[^>]*color-scheme[^>]*>/gi, "");
  if (/<head\b[^>]*>/i.test(next)) {
    next = next.replace(/<head\b[^>]*>/i, (head) => `${head}${base}`);
  } else {
    next = `<!DOCTYPE html><html><head>${base}</head><body>${next}</body></html>`;
  }
  if (/<\/body>/i.test(next)) {
    next = next.replace(/<\/body>/i, `${guard}</body>`);
  } else {
    next += guard;
  }
  return next;
}

/** Eigenständiges HTML-Dokument für die isolierte Mail-Anzeige. */
export function buildMailFrameSrcDoc(html: string): string | null {
  const source = html.trim();
  if (!source) return null;
  ensureLinkHooks();
  const sanitized = DOMPurify.sanitize(source, {
    USE_PROFILES: { html: true },
    WHOLE_DOCUMENT: true,
    ADD_ATTR: ["target", "rel"],
  });
  if (!mailHtmlHasVisibleContent(sanitized)) return null;
  return injectFrameChrome(rewriteStyleTags(sanitized));
}
